import { useState, useEffect } from 'react'
import './dataEntry.css'
import { useLanguage } from '../../context/LanguageContext'
import { looksLikeSqlInjection } from '../../utils/sanitize'

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'

// Resolves a label that may be a plain string or a { de, en, ... } translation object
function resolveLabel(label, language) {
  if (!label) return ''
  if (typeof label === 'string') return label
  return label[language] || label['de'] || label['en'] || Object.values(label)[0] || ''
}

// ── Time picker ───────────────────────────────────────────────────────────────
// Renders two <select> dropdowns (hour / minute) so only valid times are selectable.
// minTime and maxTime are "HH:MM" strings or undefined.

const MINUTE_STEP = 15

function parseHM(str) {
  if (!str) return null
  const [h, m] = str.split(':').map(Number)
  return { h, m: m ?? 0 }
}

function pad2(n) { return String(n).padStart(2, '0') }

function TimePicker({ fieldId, value, onChange, errorClass, minTime, maxTime }) {
  const min = parseHM(minTime)
  const max = parseHM(maxTime)
  const [hh, mm] = value ? value.split(':') : ['', '']
  const selectedH = hh !== '' ? parseInt(hh, 10) : null

  const validHours = []
  for (let h = 0; h <= 23; h++) {
    if (min && h < min.h) continue
    if (max && h > max.h) continue
    validHours.push(h)
  }

  function validMinutesFor(h) {
    const mins = []
    for (let m = 0; m < 60; m += MINUTE_STEP) {
      if (min && h === min.h && m < min.m) continue
      if (max && h === max.h && m > max.m) continue
      mins.push(m)
    }
    return mins
  }

  const validMinutes = selectedH !== null ? validMinutesFor(selectedH) : []

  function handleHourChange(e) {
    const newH = e.target.value
    if (!newH) { onChange(''); return }
    const h = parseInt(newH, 10)
    const mins = validMinutesFor(h)
    if (mins.length === 0) { onChange(''); return }
    // Keep current minute if still valid, otherwise pick first valid
    const currentM = mm !== '' ? parseInt(mm, 10) : null
    const chosenM = currentM !== null && mins.includes(currentM) ? currentM : mins[0]
    onChange(`${pad2(h)}:${pad2(chosenM)}`)
  }

  function handleMinuteChange(e) {
    if (selectedH === null) return
    onChange(`${pad2(selectedH)}:${pad2(parseInt(e.target.value, 10))}`)
  }

  return (
    <div className="time-picker" id={fieldId}>
      <select className={errorClass} value={hh} onChange={handleHourChange}>
        <option value="">--</option>
        {validHours.map(h => (
          <option key={h} value={pad2(h)}>{pad2(h)}</option>
        ))}
      </select>
      <span className="time-picker-sep">:</span>
      <select className={errorClass} value={mm} onChange={handleMinuteChange} disabled={selectedH === null}>
        <option value="">--</option>
        {validMinutes.map(m => (
          <option key={m} value={pad2(m)}>{pad2(m)}</option>
        ))}
      </select>
    </div>
  )
}

// ── Field renderer ────────────────────────────────────────────────────────────

const Field = ({ field, value, onChange, error, errorMsg, language, minTime, t }) => {
  const label = resolveLabel(field.label, language)

  const baseProps = {
    id:        field.id,
    name:      field.id,
    className: error ? 'error' : '',
    value:     value ?? '',
    onChange:  e => onChange(field.id, e.target.value),
  }

  const today = new Date().toISOString().split('T')[0]

  let input
  if (field.type === 'textarea') {
    input = <textarea {...baseProps} rows={3} />
  } else if (field.type === 'select') {
    input = (
      <select {...baseProps}>
        <option value="">—</option>
        {(field.options ?? []).map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    )
  } else if (field.type === 'checkbox') {
    return (
      <div className="viGuard__dataEntry-field">
        <label htmlFor={field.id} className="de-checkbox-label">
          <input
            id={field.id}
            type="checkbox"
            checked={!!value}
            onChange={e => onChange(field.id, e.target.checked)}
          />
          {label}
        </label>
        {error && <span className="error-msg">— {errorMsg || t.required}</span>}
      </div>
    )
  } else if (field.type === 'date') {
    input = <input {...baseProps} type="date" min={today} />
  } else if (field.type === 'time') {
    input = (
      <TimePicker
        fieldId={field.id}
        value={value ?? ''}
        onChange={v => onChange(field.id, v)}
        errorClass={error ? 'error' : ''}
        minTime={minTime}
        maxTime={field.openUntil || undefined}
      />
    )
  } else {
    const typeMap = { email: 'email', phone: 'tel' }
    input = <input {...baseProps} type={typeMap[field.type] ?? 'text'} />
  }

  return (
    <div className="viGuard__dataEntry-field">
      <label htmlFor={field.id}>
        {label}
        {!field.required && <span className="de-field-optional"> ({t.optional})</span>}
      </label>
      {input}
      {error && <span className="error-msg">— {errorMsg || t.required}</span>}
    </div>
  )
}

// ── DynamicDataEntry ──────────────────────────────────────────────────────────

const DynamicDataEntry = ({ stepConfig }) => {
  const {
    language,
    setVisitorData,
    visitorData,
    setSessionId,
    tenantSlug,
    setAlreadyCheckedIn,
    setQrBase64,
    startSession,
    goNext,
    goToConfirmation,
    allTranslations,
    flowMode,
    setSafetyRulesReadAt,
    setSkipSafety,
  } = useLanguage()

  const t      = allTranslations[language] || allTranslations['en']
  const fields = stepConfig?.fields ?? []
  const title  = resolveLabel(stepConfig?.title, language) || t.data_entry_title || 'Ihre Angaben'

  // Initialise form state from existing visitorData (in case of multi-page flow)
  const [form,       setForm]       = useState(() => {
    const base = {}
    fields.forEach(f => { base[f.id] = visitorData?.[f.id] ?? '' })
    return base
  })
  const [errors,     setErrors]     = useState({})
  const [errorMsgs,  setErrorMsgs]  = useState({})
  const [checking,   setChecking]   = useState(false)
  const [existing,   setExisting]   = useState(null)

  // Re-sync if fields change (e.g. different step config loaded)
  useEffect(() => {
    setForm(prev => {
      const next = {}
      fields.forEach(f => { next[f.id] = prev[f.id] ?? visitorData?.[f.id] ?? '' })
      return next
    })
    setErrors({})
    setErrorMsgs({})
  }, [stepConfig])

  const timeFieldId = fields.find(f => f.type === 'time')?.id
  const dateFieldId = fields.find(f => f.type === 'date')?.id

  const handleChange = (id, value) => {
    setForm(prev => {
      const next = { ...prev, [id]: value }
      // Clear time when the date changes to avoid stale invalid selections
      if (id === dateFieldId && timeFieldId) next[timeFieldId] = ''
      return next
    })
    if (errors[id]) {
      setErrors(prev => ({ ...prev, [id]: false }))
      setErrorMsgs(prev => ({ ...prev, [id]: undefined }))
    }
  }

  const computeMinTime = (field) => {
    if (field.type !== 'time') return undefined
    const today = new Date().toISOString().split('T')[0]
    const selectedDate = dateFieldId ? form[dateFieldId] : null
    const nowHHMM = new Date().toTimeString().slice(0, 5)
    if (selectedDate === today) {
      // Later of openFrom and current time
      return field.openFrom && field.openFrom > nowHHMM ? field.openFrom : nowHHMM
    }
    return field.openFrom || undefined
  }

  const validate = () => {
    const e    = {}
    const msgs = {}
    const today = new Date().toISOString().split('T')[0]

    fields.forEach(f => {
      const val = form[f.id]

      if (f.required && (val === undefined || val === null || val === '' || val === false)) {
        e[f.id] = true; msgs[f.id] = t.required
      }

      if (f.type === 'email' && val && !/\S+@\S+\.\S+/.test(val)) {
        e[f.id] = true; msgs[f.id] = t.error_email_invalid
      }

      if (typeof val === 'string' && looksLikeSqlInjection(val)) {
        e[f.id] = true; msgs[f.id] = t.error_invalid_input
      }

      if (f.type === 'date' && val && val < today) {
        e[f.id] = true; msgs[f.id] = t.error_date_past
      }

      if (f.type === 'time' && val) {
        const selectedDate = dateFieldId ? form[dateFieldId] : null

        if (selectedDate === today) {
          const nowHHMM = new Date().toTimeString().slice(0, 5)
          if (val <= nowHHMM) {
            e[f.id] = true; msgs[f.id] = t.error_time_past
          }
        }

        if (!e[f.id] && f.openFrom && val < f.openFrom) {
          e[f.id] = true
          msgs[f.id] = t.error_time_before_open.replace('{time}', f.openFrom)
        }
        if (!e[f.id] && f.openUntil && val > f.openUntil) {
          e[f.id] = true
          msgs[f.id] = t.error_time_after_close.replace('{time}', f.openUntil)
        }
      }
    })

    setErrorMsgs(msgs)
    return e
  }

  const handleSubmit = async () => {
    const newErrors = validate()
    if (Object.keys(newErrors).length > 0) { setErrors(newErrors); return }

    if (!tenantSlug) { setErrors({ _general: true }); return }

    // Merge this page's data into visitorData (multi-page support)
    const merged = { ...(visitorData ?? {}), ...form }
    setVisitorData(merged)

    // Only do the duplicate-check on the step that has email + visitDate
    const hasEmail     = fields.some(f => f.id === 'email')
    const hasVisitDate = fields.some(f => f.id === 'visitDate')

    if (hasEmail && hasVisitDate && merged.email && merged.visitDate) {
      setChecking(true)
      try {
        const res  = await fetch(`${BACKEND_URL}/api/checkin/check-existing`, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'X-Tenant-Slug': tenantSlug },
          body:    JSON.stringify({
            email:     merged.email,
            visitDate: merged.visitDate,
            firstName: merged.firstName,
            lastName:  merged.lastName,
            company:   merged.company,
            flowMode,
          }),
        })
        const data = await res.json()

        if (data.exists) {
          setExisting(data)
          setSessionId(data.sessionId)
          setQrBase64(data.qrBase64)
          setAlreadyCheckedIn(true)
          return
        }
      } catch {
        // Network error — continue without duplicate check
      } finally {
        setChecking(false)
      }

      // Returning visitor check — skip safety rules + quiz if read within last year
      if (merged.email && merged.firstName && merged.lastName) {
        try {
          const params = new URLSearchParams({
            email:     merged.email,
            firstName: merged.firstName,
            lastName:  merged.lastName,
            company:   merged.company || '',
          })
          const rvRes  = await fetch(`${BACKEND_URL}/api/checkin/returning-visitor?${params}`, {
            headers: { 'X-Tenant-Slug': tenantSlug },
          })
          if (rvRes.ok) {
            const rv = await rvRes.json()
            if (rv.canSkip) {
              setSafetyRulesReadAt(rv.lastReadAt)
              setSkipSafety(true)
              startSession()
              goToConfirmation()
              return
            }
          }
        } catch {
          // Network error — continue to full flow
        }
      }
    }

    startSession()
    goNext()
  }

  // ── Already checked-in notice ─────────────────────────────────────────────
  if (existing) {
    const name    = `${existing.firstName} ${existing.lastName}`
    const message = t.existing_checkin_message
      .replace('{name}', name)
      .replace('{date}', form.visitDate || '')

    return (
      <div className="viGuard__dataEntry">
        <div className="viGuard__dataEntry-content">
          <div className="viGuard__dataEntry-confirm">
            <div className="checkmark">!</div>
            <h2 className="gradient__text">{t.existing_checkin_title}</h2>
            <p>{message}</p>
            <button onClick={goToConfirmation}>{t.existing_checkin_button}</button>
          </div>
        </div>
      </div>
    )
  }

  // ── Form ──────────────────────────────────────────────────────────────────
  return (
    <div className="viGuard__dataEntry">
      <div className="viGuard__dataEntry-content">
        <h1 className="gradient__text">{title}</h1>

        {errors._general && (
          <div className="de-general-error">
            {t.no_tenant_error}
          </div>
        )}

        <div className="viGuard__dataEntry-form">
          {fields.map(field => (
            <Field
              key={field.id}
              field={field}
              value={form[field.id]}
              onChange={handleChange}
              error={!!errors[field.id]}
              errorMsg={errorMsgs[field.id]}
              language={language}
              minTime={computeMinTime(field)}
              t={t}
            />
          ))}

          {fields.length === 0 && (
            <p className="de-no-fields">
              {t.no_fields_configured}
            </p>
          )}

          <div className="viGuard__dataEntry-submit">
            <button type="button" onClick={handleSubmit} disabled={checking}>
              {checking ? t.checking : t.next_button}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DynamicDataEntry
